import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import { getFilterOperatorsForType } from 'src/utils/blueprintUtils'
import type {
  FilterNode,
  FilterCondition,
  FilterOperator,
  BlueprintNode
} from 'src/types/queries'
import type { CellType } from 'src/types/tableDataSet'
import { StringFilterValue } from './StringFilterValue/StringFilterValue'

import './FilterEditor.css'

interface BoundaryInputProps {
  uiType: CellType | undefined
  value: string | number | boolean | undefined
  onChange: (value: string | number | boolean) => void
  blueprint: BlueprintNode
  column: string
  listId: string
}

function BoundaryInput({
  uiType,
  value,
  onChange,
  blueprint,
  column,
  listId
}: BoundaryInputProps) {
  const intl = useIntl()
  if (uiType === 'number') {
    return (
      <input
        type="number"
        className="boundary-input"
        value={typeof value === 'number' ? value : ''}
        placeholder={intl.formatMessage({
          id: 'filter-editor.condition.boundary-placeholder.number'
        })}
        onChange={(e) => {
          const raw = e.target.value
          const n = raw === '' ? '' : Number(raw)
          onChange(typeof n === 'number' && Number.isNaN(n) ? '' : n)
        }}
      />
    )
  }

  if (uiType === 'bool') {
    return (
      <select
        className="boundary-input"
        value={typeof value === 'boolean' ? String(value) : ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? '' : e.target.value === 'true')
        }
      >
        <option hidden disabled value="">
          —
        </option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    )
  }

  if (uiType === 'string') {
    return (
      <StringFilterValue
        blueprint={blueprint}
        column={column}
        listId={listId}
        value={value}
        onChange={onChange}
        placeholder={intl.formatMessage({
          id: 'filter-editor.condition.boundary-placeholder.string'
        })}
      />
    )
  }

  return (
    <input
      type="text"
      className="boundary-input"
      value={typeof value === 'string' ? value : ''}
      placeholder={intl.formatMessage({
        id: 'filter-editor.condition.boundary-placeholder'
      })}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

interface FilterEditorProps {
  node: FilterNode
  onChange: (node: FilterNode) => void
}

export default function FilterEditor({ node, onChange }: FilterEditorProps) {
  const intl = useIntl()
  const { colSpec = [] } = useBlueprintColumns(node.source)

  const handleConditionChange = (
    index: number,
    changes: Partial<FilterCondition>
  ) => {
    const newConditions = node.conditions.map((cond, i) =>
      i === index ? { ...cond, ...changes } : cond
    )
    onChange({ ...node, conditions: newConditions })
  }

  const handleColChange = (index: number, newCol: string) => {
    const uiType = colSpec.find((c) => c.name === newCol)?.uiType
    const allowed = getFilterOperatorsForType(uiType)
    const current = node.conditions[index].condition
    const condition = allowed.includes(current) ? current : '='
    handleConditionChange(index, { col: newCol, condition, boundary: '' })
  }

  const handleAddCondition = () => {
    const newCondition: FilterCondition = {
      col: '',
      condition: '=',
      boundary: ''
    }
    onChange({ ...node, conditions: [...node.conditions, newCondition] })
  }

  const handleRemoveCondition = (index: number) => {
    const newConditions = node.conditions.filter((_, i) => i !== index)
    onChange({ ...node, conditions: newConditions })
  }

  const handleLogicChange = (logic: 'AND' | 'OR') => {
    onChange({ ...node, logic })
  }

  const canRemove = node.conditions.length > 1

  return (
    <div id="filter-editor">
      <header>
        <h3>
          <FormattedMessage id={`operation-editor.option.${node.type}`} />
        </h3>
      </header>
      <div className="logic-selector">
        <label>
          <FormattedMessage id="filter-editor.logic.label" />
          {': '}
        </label>
        <select
          disabled={node.conditions.length < 2}
          value={node.logic ?? 'AND'}
          onChange={(e) => handleLogicChange(e.target.value as 'AND' | 'OR')}
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>

      <ul className="conditions-list">
        <li className="condition condition-header">
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.col-title" />
            </h4>
          </div>
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.operator-title" />
            </h4>
          </div>
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.boundary-title" />
            </h4>
          </div>
        </li>
        {node.conditions.map((cond, index) => {
          const uiType = colSpec.find((c) => c.name === cond.col)?.uiType
          const operators = getFilterOperatorsForType(uiType)

          return (
            <li key={index} className="condition">
              <div className="condition-separator">
                <button
                  type="button"
                  className="remove-condition"
                  onClick={() => handleRemoveCondition(index)}
                  disabled={!canRemove}
                >
                  <span className="icon icon-bin" />
                </button>
                <select
                  className="col-select"
                  value={cond.col}
                  onChange={(e) => handleColChange(index, e.target.value)}
                >
                  <option hidden disabled value="">
                    {intl.formatMessage({
                      id: 'filter-editor.condition.col-placeholder'
                    })}
                  </option>
                  {colSpec.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="condition-separator">
                <select
                  className="operator-select"
                  value={cond.condition}
                  onChange={(e) =>
                    handleConditionChange(index, {
                      condition: e.target.value as FilterOperator
                    })
                  }
                >
                  {operators.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="condition-separator">
                <BoundaryInput
                  uiType={uiType}
                  value={cond.boundary}
                  onChange={(boundary) =>
                    handleConditionChange(index, { boundary })
                  }
                  blueprint={node.source}
                  column={cond.col}
                  listId={`filter-value-${index}`}
                />
              </div>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        className="add-condition"
        onClick={handleAddCondition}
      >
        <span className="icon icon-plus" />
      </button>
    </div>
  )
}
